import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames/bind';
import { Swiper, type SwiperProps,SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';

import { DEVICE_PLATFORM, useDeviceType } from '@autopass-web/domain';
import { CoreIcon } from '@autopass-web/mochi-icons';

import Spinner from '../spinner-mochi/spinner-mochi';
import styles from './index.module.sass';

import 'swiper/css';
const cx = classNames.bind(styles);

interface indicatorProps {
  indicatorPosition: string;
  index: number;
  itemsLength: number;
  isDraging: boolean;
  itemMovingDirection: string;
  itemMovingRate: number;
  markerStretchDirection: string;
}

const Indicators = ({
  indicatorPosition,
  index,
  itemsLength,
  isDraging,
  itemMovingDirection,
  itemMovingRate,
  markerStretchDirection,
}: indicatorProps) => {
  const indicatorWidth = 6;
  const indicatorMargin = 4;
  const indicatorGroupWidth = indicatorWidth + indicatorMargin;
  const isFirstIndex = index === 0;
  const isLastIndex = index === itemsLength - 1;
  const isOverHalf = Math.abs(itemMovingRate) > 0.5;

  const markerDirectionOffset = () => {
    if (isDraging) {
      // 拖曳中
      if (itemMovingDirection === 'next') {
        if (isOverHalf) {
          if (isLastIndex) return 0;
          return (itemsLength - 2 - index) * indicatorGroupWidth;
        } else {
          return index * indicatorGroupWidth;
        }
      } else {
        if (isOverHalf) {
          if (isFirstIndex) return 0;
          return (index - 1) * indicatorGroupWidth;
        } else {
          return (itemsLength - 1 - index) * indicatorGroupWidth;
        }
      }
    } else {
      return markerStretchDirection === 'left'
        ? index * indicatorGroupWidth
        : (itemsLength - 1 - index) * indicatorGroupWidth;
    }
  };

  const markerWidth = useCallback(() => {
    const indicatorsWidth = indicatorWidth * 3 + indicatorMargin * 2;

    // 在第一個及最後一個 indicator 時不做變化
    if (
      (isFirstIndex && itemMovingDirection === 'prev') ||
      (isLastIndex && itemMovingDirection === 'next')
    )
      return indicatorWidth;

    if (isOverHalf) {
      return (
        indicatorsWidth -
        Math.abs(itemMovingRate) * 2 * (indicatorWidth + indicatorMargin)
      );
    } else {
      return (
        indicatorWidth +
        Math.abs(itemMovingRate) * 2 * (indicatorWidth + indicatorMargin)
      );
    }
  }, [
    isFirstIndex,
    isLastIndex,
    isOverHalf,
    itemMovingDirection,
    itemMovingRate,
    indicatorGroupWidth,
    itemsLength,
  ]);

  const indicatorList = [];

  const indicatorGroup = cx({
    'indicator-group': true,
    [indicatorPosition]: true,
  });

  for (let i = 0; i < itemsLength; i++) {
    const indicatorClass = cx({
      indicator: true,
      active: i === index,
    });
    indicatorList.push(<li className={indicatorClass} key={i}></li>);
  }

  const width =
    indicatorWidth * itemsLength + indicatorMargin * (itemsLength - 1);

  return (
    <ul
      className={indicatorGroup}
      style={{
        width,
        left:
          indicatorPosition === 'default'
            ? 'inherit'
            : `calc(50% - ${width / 2}px)`,
      }}
    >
      <div
        className={styles.marker}
        style={{
          transition: isDraging ? '' : 'all 0.2s ease',
          width: `${markerWidth()}px`,
          [markerStretchDirection]: `${markerDirectionOffset()}px`,
        }}
      />
      {indicatorList}
    </ul>
  );
};

const Arrow = ({
  index,
  itemsLength,
  onPrev,
  onNext,
}: {
  index: number;
  itemsLength: number;
  onPrev?: () => void;
  onNext?: () => void;
}) => {
  const arrowPreviousClass = cx({
    arrow: true,
    prev: true,
    show: index !== 0,
  });

  const arrowNextClass = cx({
    arrow: true,
    next: true,
    show: index !== itemsLength - 1,
  });

  return (
    <>
      <div className={arrowPreviousClass} onClick={onPrev}>
        <CoreIcon src="/assets/chevron-left.svg" />
      </div>
      <div className={arrowNextClass} onClick={onNext}>
        <CoreIcon src="/assets/chevron-right.svg" />
      </div>
    </>
  );
};

interface carouselProps {
  style?: any;
  className?: any;
  loading: boolean;
  theme?: string;
  items: Array<any>;
  indicator?: boolean;
  indicatorPosition?: 'default' | 'inside';
  arrow?: boolean;
  full?: boolean;
  spaceBetween?: SwiperProps['spaceBetween'];
  slidesPerView?: SwiperProps['slidesPerView'];
  breakpoints?: SwiperProps['breakpoints'];
  onSwipe?: (index: number) => void;
}

const Carousel = forwardRef<HTMLInputElement, carouselProps>(
  (
    {
      style = {},
      className,
      loading,
      theme = 'dark',
      items,
      indicator = true,
      full = false,
      indicatorPosition = 'default',
      arrow = false,
      spaceBetween = 20,
      slidesPerView,
      breakpoints = {
        320: {
          slidesPerView: slidesPerView ?? 'auto',
        },
        768: {
          slidesPerView: slidesPerView ?? 2,
        },
        1024: {
          slidesPerView: slidesPerView ?? 3,
        },
      },
      onSwipe,
    },
    ref,
  ) => {
    const itemsLength = items ? items.length : 0;
    const [isDraging, setIsDraging] = useState(false);
    const [itemMovingRate, setItemMovingRate] = useState(0);
    const [itemMovingDirection, setItemMovingDirection] = useState('next');
    const [markerStretchDirection, setMarkerStretchDirection] =
      useState('left');
    const [index, setIndex] = useState(0);
    const swiperRef = useRef<SwiperType>();
    const device = useDeviceType();
    const isMobile = device === DEVICE_PLATFORM.MOBILE;

    useImperativeHandle<any, any>(
      ref,
      () => ({
        next() {
          swiperRef.current?.slideNext();
        },
        back() {
          swiperRef.current?.slidePrev();
        },
        reset() {
          setIndex(0);
          swiperRef.current?.slideTo(0);
        },
      }),
      [swiperRef, index],
    );

    const carouselClass = cx({
      carousel: true,
      [theme]: true,
    });

    const swiperClass = cx({
      swiper: true,
      full,
    });

    return (
      <div className={`${carouselClass} ${className}`} style={style}>
        {loading ? (
          <div className={styles.loading}>
            <Spinner />
          </div>
        ) : (
          <Swiper
            className={swiperClass}
            breakpoints={breakpoints}
            spaceBetween={spaceBetween}
            draggable
            onSliderFirstMove={() => setIsDraging(true)}
            onSlideChangeTransitionStart={() => setIsDraging(false)}
            onSetTranslate={(e, currentPosition) => {
              const widthCal = () => {
                return full ? e.width : e.width / 1.15;
              };

              const rate = (currentPosition + widthCal() * index) / widthCal();
              setItemMovingRate(rate);
              setItemMovingDirection(rate < 0 ? 'next' : 'prev');
              setMarkerStretchDirection(() => {
                if (Math.abs(rate) < 0.5) {
                  return rate < 0 ? 'left' : 'right';
                } else {
                  return rate < 0 ? 'right' : 'left';
                }
              });
            }}
            onSlideChange={(swiper) => {
              setIndex(swiper.activeIndex);
              if (onSwipe) onSwipe(swiper.activeIndex);
            }}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
          >
            {items.map((item) => {
              return (
                <SwiperSlide
                  style={{ width: full ? '100%' : 'calc(100% - 40px)' }}
                >
                  {item}
                </SwiperSlide>
              );
            })}
          </Swiper>
        )}
        {arrow && items.length > 1 && (
          <Arrow
            index={index}
            itemsLength={itemsLength}
            onPrev={swiperRef.current?.slidePrev}
            onNext={swiperRef.current?.slideNext}
          />
        )}
        {indicator && itemsLength > 1 && (isMobile || full) && (
          <Indicators
            indicatorPosition={indicatorPosition}
            index={index}
            itemsLength={itemsLength}
            isDraging={isDraging}
            itemMovingDirection={itemMovingDirection}
            itemMovingRate={itemMovingRate}
            markerStretchDirection={markerStretchDirection}
          />
        )}
      </div>
    );
  },
);

export default Carousel;
